/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, Inject, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {finalize} from 'rxjs';

import template from './political-sponsor-budget-comparison-page.component.html';
import {SIMPLE_PAGE_IMPORTS} from './simple-page-imports';
import {SignalPageState} from '../../common/controller/signal-page-state';
import {
  PoliticalSponsorApiService,
  PoliticalSponsorBudgetGroup
} from '../../political-entity/political-sponsor-api.service';
import {WINDOW} from '../../angular-platform-providers';

const EXPORT_FILE_NAME = 'political-advertisers.xlsx';

@Component({
  standalone: true,
  imports: SIMPLE_PAGE_IMPORTS,
  selector: 'adn-political-sponsor-budget-comparison-page',
  template
})
export class PoliticalSponsorBudgetComparisonPageComponent implements OnInit {
  readonly page: SignalPageState<PoliticalSponsorBudgetGroup, Record<string, never>>;
  readonly exportPending = signal(false);
  readonly exportFailed = signal(false);

  constructor(
    private readonly politicalSponsorApi: PoliticalSponsorApiService,
    @Inject(WINDOW) private readonly window: Window & typeof globalThis,
    private readonly destroyRef: DestroyRef
  ) {
    this.page = new SignalPageState(
      (query, page, pageSize) => this.politicalSponsorApi.budgetComparisons({...query, page, pageSize}),
      50,
      this.destroyRef
    );
  }

  ngOnInit(): void {
    this.page.load({}, true);
  }

  downloadAdvertisers(): void {
    if (this.exportPending()) {
      return;
    }
    this.exportPending.set(true);
    this.exportFailed.set(false);
    this.politicalSponsorApi.exportAdvertisers().pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.exportPending.set(false))
    ).subscribe({
      next: (blob) => this.downloadBlob(blob),
      error: () => this.exportFailed.set(true)
    });
  }

  private downloadBlob(blob: Blob): void {
    const url = this.window.URL.createObjectURL(blob);
    const link = this.window.document.createElement('a');
    link.href = url;
    link.download = EXPORT_FILE_NAME;
    link.rel = 'noopener';
    link.style.display = 'none';
    this.window.document.body.appendChild(link);
    try {
      link.click();
    } finally {
      link.remove();
      this.window.URL.revokeObjectURL(url);
    }
  }
}
